/* eslint-disable */
// @ts-nocheck

/**
 * - Domain placeholder interpolation (e.g. {account}.example.com)
 * - Safer double-slash tidy (no look-behind)
 * - Arrays & nested query params (URLSearchParams)
 * - Optional "splat" support: params whose key ends with "*" keep "/" (e.g. {path*})
 * - Overloads: route(name), route(name, true), route(name, {id:1}), route(name, "abc")
 */

export type RouteDef = {
    uri: string; // "/" or e.g. "terms-conditions", "users/{id?}"
    domain?: string | null; // e.g. "wp-ecom.local" or "https://shop.example.com" or pattern "{acct}.example.com"
    whitelisted?: true; // must be true to be accepted
};

export type RoutesMap = Record<string, RouteDef>;

type ParamScalar = string | number | boolean;
type ParamBag = Record<string, unknown>;

class Router {
    private readonly base: string;
    private readonly routes: RoutesMap;

    constructor(opts: { baseUrl: string; routes: RoutesMap }) {
        this.base = opts.baseUrl.replace(/\/+$/, "");
        this.routes = Object.freeze({ ...opts.routes });
    }

    // Overloads
    public route(name: string): string;
    public route(name: string, absolute: boolean): string;
    public route(name: string, params: ParamBag | ParamScalar | undefined, absolute?: boolean): string;

    public route(
        name: string,
        paramsOrAbsolute?: ParamBag | ParamScalar | boolean,
        absoluteArg?: boolean
    ): string {
        const def = this.routes[name];
        if (!def || !def.whitelisted) {
            const available = import.meta?.env?.DEV
                ? ` Available: ${Object.keys(this.routes).sort().join(", ")}`
                : "";
            throw new Error(`Unknown or disallowed route: "${name}".${available}`);
        }

        // If someone accidentally put an absolute URL in `uri`, just return it.
        if (/^https?:\/\//i.test(def.uri)) {
            return def.uri;
        }

        // Normalise (params, absolute)
        const absolute =
            typeof paramsOrAbsolute === "boolean" ? paramsOrAbsolute : !!absoluteArg;

        const params: ParamBag =
            typeof paramsOrAbsolute === "boolean" || paramsOrAbsolute == null
                ? {}
                : typeof paramsOrAbsolute === "object"
                    ? (paramsOrAbsolute as ParamBag)
                    : coercePrimitiveParams(def.uri, paramsOrAbsolute);

        // 1) Interpolate params into path (supports {x} and {x?})
        let path = String(def.uri).replace(/\{(\w+)\??}/g, (m, key: string) => {
            if (Object.prototype.hasOwnProperty.call(params, key)) {
                return encodeForPath(key, (params as Record<string, unknown>)[key]);
            }
            // leave placeholder; optional segments stripped next
            return m;
        });

        // 2) Strip unused optional segments like "/{id?}"
        path = path.replace(/\/\{(\w+)\?}/g, (_m, key: string) =>
            Object.prototype.hasOwnProperty.call(params, key)
                ? "/" + encodeForPath(key, (params as Record<string, unknown>)[key])
                : ""
        );

        // 3) Any required params left in the path?
        if (/\{(\w+)}/.test(path)) {
            const missing = Array.from(path.matchAll(/\{(\w+)}/g)).map(m => m[1]);
            throw new Error(`Missing parameter(s) ${missing.join(", ")} for route "${name}"`);
        }

        // 4) Origin selection (with domain placeholder interpolation)
        const origin = def.domain ? normaliseOrigin(def.domain, params) : absolute ? this.base : "";

        // If domain still contains {x}, they're missing
        if (/\{(\w+)}/.test(String(origin))) {
            const missing = Array.from(String(origin).matchAll(/\{(\w+)}/g)).map(m => m[1]);
            throw new Error(`Missing domain parameter(s) ${missing.join(", ")} for route "${name}"`);
        }

        // 5) Extra params -> query string
        const usedKeys = new Set(
            Array.from(String(def.uri).matchAll(/\{(\w+)\??}/g)).map(m => m[1])
        );
        const qs = buildQuery(params, usedKeys);

        // 6) Join and tidy slashes
        const slash = path.startsWith("/") || path === "" ? "" : "/";
        const url = tidySlashes(`${origin}${slash}${path}`);

        return `${url}${qs}`;
    }
}

/** Encode a value for a path segment; keep "/" if key ends with "*" (splat). */
function encodeForPath(k: string, v: unknown) {
    const str = String(v);
    if (/\*$/.test(k)) {
        // Preserve slashes: encode each segment then put "/" back
        return str
            .split("/")
            .map(seg => encodeURIComponent(seg))
            .join("/")
            .replace(/%2F/gi, "/");
    }
    return encodeURIComponent(str);
}

/** Interpolate {x} placeholders inside a string template using params. */
function fillPlaceholders(
    template: string,
    params: Record<string, unknown>,
    encode = encodeURIComponent
) {
    return template.replace(/\{(\w+)\??}/g, (_m, key) => {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            return encode(String((params as Record<string, unknown>)[key]));
        }
        return _m;
    });
}

/** Normalise a domain (with optional scheme) and interpolate placeholders. */
function normaliseOrigin(d?: string | null, params?: Record<string, unknown>) {
    if (!d) return "";
    let s = String(d).trim().replace(/\/+$/, "");
    s = fillPlaceholders(s, params ?? {}, encodeURIComponent);
    if (/^https?:\/\//i.test(s)) return s;
    return `${window.location.protocol}//${s}`;
}

/** Safer “remove duplicate slashes” that keeps the scheme "://". */
function tidySlashes(u: string) {
    return u.replace(/([^:])\/{2,}/g, "$1/");
}

/** Build query string; supports arrays & nested objects. */
function buildQuery(params: Record<string, unknown>, usedKeys: Set<string>): string {
    const qp = new URLSearchParams();

    const add = (k: string, v: unknown) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v)) {
            v.forEach(item => qp.append(`${k}[]`, String(item)));
        } else if (typeof v === "object") {
            Object.entries(v as Record<string, unknown>).forEach(([sk, sv]) => add(`${k}[${sk}]`, sv));
        } else if (typeof v === "boolean") {
            // Coerce booleans to 1/0 for broader backend compatibility
            qp.append(k, v ? "1" : "0");
        } else {
            qp.append(k, String(v));
        }
    };

    Object.entries(params).forEach(([k, v]) => {
        if (!usedKeys.has(k)) add(k, v);
    });

    const s = qp.toString();
    return s ? `?${s}` : "";
}

/** Turn a primitive into params.
 * If exactly one placeholder exists in the URI, map to that key.
 * Otherwise, fall back to { value: primitive }.
 * You can rename "value" if you prefer.
 */
function coercePrimitiveParams(uri: string, val: ParamScalar): ParamBag {
    const placeholders = Array.from(uri.matchAll(/\{(\w+)\??}/g)).map(m => m[1]);
    if (placeholders.length === 1) {
        return { [placeholders[0]]: val };
    }
    return { value: val };
}

// ———————————————————————————————————————
// Singleton + helpers
// ———————————————————————————————————————

let _router: Router | null = null;

/** Idempotent: call on first load and on each Inertia navigation. */
export function initRouter(manifest: Record<string, RouteDef> | undefined, baseUrl: string) {
    const filtered: RoutesMap = Object.fromEntries(
        Object.entries(manifest || {}).filter(([, def]) => def && (def as RouteDef).whitelisted)
    );

    // Freeze inner defs for light hardening
    Object.keys(filtered).forEach(k => Object.freeze((filtered as Record<string, unknown>)[k]!));

    _router = new Router({ baseUrl, routes: filtered });
}

// Public overloads (for call sites)
// Default `absolute` stays `true` to match your previous behaviour.
export function route(name: string): string;
export function route(name: string, absolute: boolean): string;
export function route(
    name: string,
    params: ParamBag | ParamScalar | undefined,
    absolute?: boolean
): string;
export function route(
    name: string,
    paramsOrAbsolute?: ParamBag | ParamScalar | boolean,
    absoluteArg: boolean = true
): string {
    if (!_router) throw new Error("Router not initialised. Call initRouter() first.");
    return _router.route(name, paramsOrAbsolute as any, absoluteArg);
}

export { Router };
